import { useEffect, useRef } from 'react';
import './MetalClawAnimation.css';

export default function MetalClawAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of opponent sprite
    const frameWidth = 192;  // Width of each frame (960px total ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // Initial player animation (slight scale before attack)
    playerEl.style.transition = 'transform 0.2s ease-in-out';
    playerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const anim = document.createElement('div');
    anim.className = 'metal-claw-anim';
    anim.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    anim.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(anim);
    animRef.current = anim;
    
    // Create metal effects at impact moment (frame 3)
    const metalEffectTimer = setTimeout(() => {
      // Flash effect with metallic color
      const flash = document.createElement('div');
      flash.className = 'metal-claw-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make opponent react
      opponentEl.classList.add('metal-claw-hit');
      
      // Remove flash and reset opponent
      setTimeout(() => {
        flash.remove();
        opponentEl.classList.remove('metal-claw-hit');
      }, 300);
      
      // Create metal spark particles
      for (let i = 0; i < 10; i++) {
        const particle = document.createElement('div');
        particle.className = 'metal-claw-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 25;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation and size for varied spark effect
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--size', `${3 + Math.random() * 6}px`);
        particle.style.setProperty('--duration', `${0.3 + Math.random() * 0.4}s`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 400 + Math.random() * 300);
      }
    }, 300); // Sync with frame 3 impact

    // Reset player position after animation
    const resetTimer = setTimeout(() => {
      playerEl.style.transition = 'transform 0.2s ease-out';
      playerEl.style.transform = '';
    }, 500);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (anim.parentNode) {
        anim.remove();
      }
      onComplete();
    }, 800);

    // Cleanup function
    return () => {
      clearTimeout(metalEffectTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.remove();
      }
      
      // Clean up any leftover elements
      const particles = container.querySelectorAll('.metal-claw-particle');
      particles.forEach(p => p.remove());
      
      const flashes = container.querySelectorAll('.metal-claw-flash');
      flashes.forEach(f => f.remove());
      
      // Reset player and opponent
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      
      if (opponentEl) {
        opponentEl.classList.remove('metal-claw-hit');
      }
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set the move type for proper coloring in the battle UI
MetalClawAnimation.moveType = "steel";
